import { notFound } from "@tanstack/react-router";
import { defaultTo, get } from "lodash-es";
import { getFeaturePageBacklinksQueryOptions } from "@/components/feature-page-backlinks/feature-page-backlinks-query";
import { getArtistFollowStatusQueryOptions } from "@/components/social/artist-follow-query";
import { getWikipediaResourceQueryOptions } from "@/components/wikipedia-resource-query";
import { requireCatalogPageId } from "@/lib/catalog-route-param";
import { ARTIST_DISCOGRAPHY_URL_CONFIG, buildFacetedSearchState } from "@/lib/faceted-search-url";
import { getSingleReleaseSearchFormat } from "@/lib/release-search-format-filter";
import { getOptionalSession } from "@/routes/_app/guards";
import { redirectCatalogRouteIfNeeded } from "@/utils/catalog-route-redirect";
import { resolveLegacyDiscogsCatalogId } from "@/utils/legacy-id-redirect";
import { prefetchForSsr } from "@/utils/loader";
import { releaseSearchFilterToFilter } from "@garimpa/lib/searchState";
import { getArtistAlbumsInfiniteQueryOptions } from "./$id/artist-albums-query";
import { getArtistBioResourceQueryOptions } from "./$id/artist-bio-resource-query";
import { getArtistDiscographyCountsQueryOptions } from "./$id/artist-discography-counts-query";
import { getArtistQueryOptions } from "./$id/artist-query";
import { discographyQueryTypeFromSearchParams } from "./$id/discography-query-type";
import { getSimilarArtistsQueryOptions } from "./$id/similar-artists-query";
interface ArtistSearch {
  q?: string;
  type?: string;
  sort?: string;
  format?: string;
  country?: string;
  label?: string[];
  company?: string[];
  year?: number[];
  filter?: ReleaseSearchFilter;
}
function shouldUseWikipediaBioFallback(wikidataId: string | null | undefined, bio: {
  text?: string | null;
  html?: string | null;
} | null | undefined): wikidataId is string {
  return Boolean(wikidataId && !bio?.text && !bio?.html);
}
const SplitLoader = async ({
  context: {
    queryClient,
    locale
  },
  params,
  deps,
  location
}) => {
  const search = location.search as ArtistSearch;
  const artistId = requireCatalogPageId(params.id);
  const discographyQueryType = discographyQueryTypeFromSearchParams(deps.type);
  const sort = defaultTo(deps.sort as DiscographySort | undefined, "oldest");
  const normalizedSearch = search.q;
  const state = buildFacetedSearchState(deps, ARTIST_DISCOGRAPHY_URL_CONFIG);
  const releaseFilter = releaseSearchFilterToFilter(get(state, "filter"), {
    allowed: ARTIST_DISCOGRAPHY_URL_CONFIG.keys,
    allTermKeys: ARTIST_DISCOGRAPHY_URL_CONFIG.allKeys
  });
  const activeFormat = defaultTo(getSingleReleaseSearchFormat(get(state, "filter")), "all");

  // `locale` is part of the client's `getArtist` useQuery input (bio text
  // is locale-scoped); without it here the SSR cache key differs from the
  // client's key, which forces a refetch on hydration and causes the
  // initial "loading" flash the user sees.
  const [artist, session] = await Promise.all([queryClient.ensureQueryData(getArtistQueryOptions({
    id: artistId,
    locale
  })), getOptionalSession({
    queryClient
  })]);
  if (!artist) {
    const legacyCatalogId = await resolveLegacyDiscogsCatalogId({
      entity: "artist",
      id: artistId,
      queryClient
    });
    if (legacyCatalogId) {
      redirectCatalogRouteIfNeeded({
        force: true,
        location,
        targetPath: `/artist/${encodeURIComponent(legacyCatalogId)}`
      });
    }
    throw notFound();
  }
  redirectCatalogRouteIfNeeded({
    location,
    targetPath: `/artist/${encodeURIComponent(artistId)}`
  });
  const artistAlbumsQueryOptions = getArtistAlbumsInfiniteQueryOptions({
    artistId,
    discographyQueryType,
    filter: releaseFilter,
    format: activeFormat,
    sort,
    search: normalizedSearch
  });
  const discographyCountsQueryOptions = getArtistDiscographyCountsQueryOptions(artistId);
  const prefetches: Promise<unknown>[] = [queryClient.prefetchInfiniteQuery(artistAlbumsQueryOptions), queryClient.prefetchQuery(discographyCountsQueryOptions), queryClient.prefetchQuery(getSimilarArtistsQueryOptions(artistId)), queryClient.prefetchQuery(getFeaturePageBacklinksQueryOptions({
    entityType: "artist",
    catalogId: artistId,
    locale
  }))];
  prefetches.push(queryClient.prefetchQuery(getArtistBioResourceQueryOptions({
    artistId,
    locale
  })));
  const wikidataId = artist.wikidataId;
  if (shouldUseWikipediaBioFallback(wikidataId, artist.bio)) {
    prefetches.push(queryClient.prefetchQuery(getWikipediaResourceQueryOptions({
      wikidataId,
      locale
    })));
  }
  if (session.user) {
    prefetches.push(queryClient.prefetchQuery(getArtistFollowStatusQueryOptions(artistId, {
      viewerId: session.user.id
    })));
  }
  await prefetchForSsr(prefetches);
  const discographyTypeCounts = queryClient.getQueryData<DiscographyTypeCounts>(discographyCountsQueryOptions.queryKey);
  return {
    artist,
    discographyQueryType,
    discographyTypeCounts
  };
};
export { SplitLoader as loader };